import { useContext, createContext, useState, useEffect } from 'react'
import { GetWarehouses, GetWarehouseByZipCode, GetMacroRegionalWarehouseByRegionalWhId, GetMacroRegionalWarehouseBySiteId } from '../Services/WarehouseService'
import { useConfig } from './ConfigProvider'
import { useAuth } from './AuthProvider'
import { useShop } from './ShopProvider'
import { useCart } from './CartProvider'
import { useOrder } from './OrderProvider'
import { useParams } from './ParamsProvider'
import useCalculateTimeDiff from '../Hooks/useCalculateTimeDiff'

const WarehouseContext = createContext()

export const useWarehouse = () => {
    return useContext(WarehouseContext)
}

export const WarehouseProvider = ({ children }) => {
    const { diffHoursFromNow } = useCalculateTimeDiff()
    const { api } = useConfig()
    const { changeSiteIdAndWhId } = useAuth()
    const { setProductsFilter } = useShop()
    const { getBasket } = useCart()
    const { orderInfo } = useOrder()
    const { availabilityCutoffTime } = useParams()
    const [selectedWarehouse, setSelectedWarehouse] = useState(null)
    const [warehouses, setWarehouses] = useState([])
    const [availableWarehousesByOTD, setAvailableWarehousesByOTD] = useState([]) //Dostępne magazyny do wyboru przy składaniu zamówienia. Zależne od OTD

    useEffect(() => {
        GetWarehouses(api).then(res => {
            setWarehouses(res.data)
        }).catch(e => {
            console.log(e)
            setWarehouses([])
        })
    }, [api])

    useEffect(() => {
        if (warehouses.length > 0) {
            setAvailableWarehousesByOTD(warehouses)
        }
    }, [warehouses])

    useEffect(() => {
        if (orderInfo.expectedDeliveryTime) {
            handleSetAvailableWarehousesByOTD()
        }
        else {
            setAvailableWarehousesByOTD(warehouses)
        }
    }, [orderInfo.expectedDeliveryTime])

    const handleSetAvailableWarehousesByOTD = async () => {
        if (!orderInfo || !orderInfo.expectedDeliveryTime || warehouses.length === 0 || !availabilityCutoffTime) {
            return
        }

        const diffHours = await diffHoursFromNow(orderInfo.expectedDeliveryTime)

        if (diffHours < 24) { // 24h do teraz
            setAvailableWarehousesByOTD(warehouses.filter(w => w.id == selectedWarehouse.id))
        } else if (diffHours < 72) { //od 24h do 72h od teraz
            setAvailableWarehousesByOTD(warehouses.filter(w => w.regionalWhId == selectedWarehouse.regionalWhId))
        } else { //ponad 72h
            setAvailableWarehousesByOTD(warehouses)
        }
    }

    const handleGetAndSaveWarehouseByZpiCone = async (zipCode) => {
        try {
            const res = await GetWarehouseByZipCode(api, zipCode)
            if (!res.data.siteId || !res.data.id) {
                return false
            }
            changeSiteIdAndWhId(res.data.siteId, res.data.id)
            setProductsFilter(prev => ({
                ...prev,
                SiteId: res.data.siteId,
                WhId: res.data.id,
            }))
            const selectedWarehouse = warehouses.find(w => w.id === res.data.id)
            setSelectedWarehouse(selectedWarehouse)
            localStorage.setItem('siteId', res.data.siteId)
            localStorage.setItem('whId', res.data.id)
            return true
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handleChangeWarehouse = (warehouse) => {
        changeSiteIdAndWhId(warehouse.siteId, warehouse.id)
        setProductsFilter(prev => ({
            ...prev,
            SiteId: warehouse.siteId,
            WhId: warehouse.id,
        }))
        setSelectedWarehouse(warehouse)
        localStorage.setItem('siteId', warehouse.siteId)
        localStorage.setItem('whId', warehouse.id)
    }

    useEffect(() => {
        if (selectedWarehouse) {
            getBasket(false)
        }
    }, [selectedWarehouse])

    const handleGetMacroRegionalWarehouseByRegionalWhId = async (regionalWhId) => {
        try {
            const res = await GetMacroRegionalWarehouseByRegionalWhId(api, regionalWhId)
            return res.data
        } catch (error) {
            console.log(error)
            return null
        }
    }

    const handleGetMacroRegionalWarehouseBySiteId = async (siteId) => {
        try {
            const res = await GetMacroRegionalWarehouseBySiteId(api, siteId)
            return res.data
        } catch (error) {
            console.log(error)
            return null
        }
    }

    return <WarehouseContext.Provider value={{
        warehouses,
        availableWarehousesByOTD,
        selectedWarehouse,
        setSelectedWarehouse,
        handleGetAndSaveWarehouseByZpiCone,
        handleChangeWarehouse,
        handleSetAvailableWarehousesByOTD,
        handleGetMacroRegionalWarehouseByRegionalWhId,
        handleGetMacroRegionalWarehouseBySiteId,
    }} >
        {children}
    </WarehouseContext.Provider>
}